import { useState, useMemo } from "react";
import { SelectChangeEvent } from "@mui/material";
import { usePostForMeAccounts, usePostForMeFeed, usePostForMeFeeds, PlatformPost, PostForMeAccountStatus } from "../../api/postForMe";
import PostsAnalyticsPresenter from "./PostsAnalyticsPresenter";
import { ALL_ACCOUNTS_ID } from "./models";

export default function PostsAnalyticsContainer() {
  const { data: accounts, isLoading: isLoadingAccounts } = usePostForMeAccounts();
  
  const connectedAccounts = useMemo(() => {
    return accounts?.filter(account => account.status === PostForMeAccountStatus.Connected) || [];
  }, [accounts]);

  const [selectedAccountId, setSelectedAccountId] = useState<string>(ALL_ACCOUNTS_ID);

  const feedQueries = usePostForMeFeeds(
    selectedAccountId === ALL_ACCOUNTS_ID ? connectedAccounts.map(a => a.id) : [],
    20,
    { refetchOnWindowFocus: false }
  );

  const { data: singleFeedData, isLoading: isLoadingSingleFeed, isFetching: isFetchingSingleFeed, error: singleFeedError } = usePostForMeFeed(
    selectedAccountId !== ALL_ACCOUNTS_ID ? selectedAccountId : null,
    undefined,
    20,
    { 
      enabled: selectedAccountId !== ALL_ACCOUNTS_ID && !!selectedAccountId,
      refetchOnWindowFocus: false
    }
  );

  const posts = useMemo(() => {
    if (selectedAccountId === ALL_ACCOUNTS_ID) {
      return feedQueries.flatMap(q => (q.data as any)?.data || []) as PlatformPost[];
    }
    return singleFeedData?.data || [];
  }, [selectedAccountId, feedQueries, singleFeedData]);

  const isLoadingFeed = selectedAccountId === ALL_ACCOUNTS_ID 
    ? feedQueries.some(q => q.isLoading || q.isFetching) 
    : isLoadingSingleFeed || isFetchingSingleFeed;

  const error = (selectedAccountId === ALL_ACCOUNTS_ID
    ? feedQueries.find(q => q.error)?.error
    : singleFeedError) as Error | null | undefined;

  const handleAccountChange = (event: SelectChangeEvent<string>) => {
    setSelectedAccountId(event.target.value);
  };
  
  return (
    <PostsAnalyticsPresenter
      accounts={connectedAccounts}
      isLoadingAccounts={isLoadingAccounts}
      selectedAccountId={selectedAccountId}
      onAccountChange={handleAccountChange}
      posts={posts}
      isLoadingFeed={isLoadingFeed}
      error={error}
    />
  );
}
